import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ComponentSync, Grid } from '@nu-art/thunderstorm-frontend';
import { cloneObj, compare, exists, voidFunction } from '@nu-art/ts-common';
import { parseExpression_Normalized } from '@app/hcs-shared/_entity/expression/antlr/parser';
import './Component_PrettyPrint_FreeTextTerm.scss';
import { Component_PrettyPrint_Binary } from '../generic/Component_PrettyPrint_Binary/Component_PrettyPrint_Binary.js';
import { Component_PrettyPrint_Comparison } from '../generic/Component_PrettyPrint_Comparison/Component_PrettyPrint_Comparison.js';
import { Component_PrettyPrint_Variadic } from '../generic/Component_PrettyPrint_Variadic/Component_PrettyPrint_Variadic.js';
import { Component_PrettyPrint_Unary } from '../generic/Component_PrettyPrint_Unary/Component_PrettyPrint_Unary.js';
import { Component_PrettyPrint_Number } from '../generic/Component_PrettyPrint_Number/Component_PrettyPrint_Number.js';
import { Component_PrettyPrint_Range } from '../generic/Component_PrettyPrint_Range/Component_PrettyPrint_Range.js';
import { Component_PrettyPrint_Atom } from '../Component_PrettyPrint_Atom/Component_PrettyPrint_Atom.js';
export class Component_PrettyPrint_FreeTextTerm extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(props, state) {
        state.term = props.term ? cloneObj(props.term) : undefined;
        state.termIndex = props.termIndex;
        //Interactiveness
        state.interactive = props.interactive;
        if ('interactiveProperties' in props) {
            state.calculationResult = props.interactiveProperties.calculationResult;
            state.actualValueMap = props.interactiveProperties.actualValueMap;
            state.suggestedValueMap = props.interactiveProperties.suggestedValueMap;
            state.selectedVariableId = props.interactiveProperties.selectedVariableId;
        }
        else {
            delete state.calculationResult;
            delete state.actualValueMap;
            delete state.suggestedValueMap;
            delete state.selectedVariableId;
        }
        if (!compare(this.state?.term || state.term)) {
            const { description, parseError } = this.parseTermText(state.termIndex, state.term?.structuredFreeText?.if);
            state.description = description;
            state.parseError = parseError;
        }
        return state;
    }
    shouldReDeriveState(nextProps) {
        return super.shouldReDeriveState(nextProps)
            || !compare(nextProps.term, this.state?.term);
    }
    //######################### Logic #########################
    parseTermText(termIndex, freeText) {
        if (!freeText)
            return { parseError: new Error(`T${termIndex}: Got no free text to parse`) };
        try {
            return { description: parseExpression_Normalized(freeText) };
        }
        catch (err) {
            this.logError('Could not parse term free text', freeText);
            return { parseError: err };
        }
    }
    //######################### Render #########################
    render() {
        if (this.state.description)
            return this.render_Term(this.state.description);
        return _jsxs("div", { children: ["T", this.state.termIndex, ": ", this.state.parseError?.message ?? 'No description or error to present'] });
    }
    render_Term = (description) => {
        return _jsxs(Grid, { className: 'free-text-term-descriptor', children: [this.render_TermTitle(), _jsxs(Grid, { className: 'description-row', children: [_jsx("div", { className: 'tree-line-connector' }), this.render_TermContent(description)] })] });
    };
    render_TermTitle = () => {
        let title = 'If the following is true ';
        const data = this.state.term?.structuredFreeText;
        title += data?.then ? `returns ${data.then}, ` : '[missing "then" value], ';
        title += data?.else ? `otherwise returns ${data.else}:` : '[missing "else" value]:';
        return _jsxs(Grid, { className: 'description-row', children: [_jsx("div", { className: 'description-row__indicator' }), _jsx("div", { className: 'description-row__title', children: title })] });
    };
    //######################### Render - Descriptions #########################
    render_TermContent = (exp) => {
        switch (exp.type) {
            case 'atom':
                return this.render_AtomExpression(exp);
            case 'number':
                return _jsx(Component_PrettyPrint_Number, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'unary':
                return _jsx(Component_PrettyPrint_Unary, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'binary':
                return _jsx(Component_PrettyPrint_Binary, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'comparison':
                return _jsx(Component_PrettyPrint_Comparison, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'variadic':
                return _jsx(Component_PrettyPrint_Variadic, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'range':
                return _jsx(Component_PrettyPrint_Range, { expression: exp, valueNode: undefined, descriptorRenderer: this.render_TermContent, interactive: this.state.interactive });
            case 'group':
                return exists(exp.expression) ? this.render_TermContent(exp.expression) : _jsx(_Fragment, {});
            default:
                return _jsxs("div", { children: ["Unhandled description type ", exp.type] });
        }
    };
    render_AtomExpression = (exp) => {
        const atom = this.state.term?.atoms[exp.atomIndex - 1];
        const onVariableSelected = ('interactiveProperties' in this.props) ? this.props.interactiveProperties.onVariableSelected : voidFunction;
        return _jsx(Component_PrettyPrint_Atom, { modules: this.props.modules, atom: atom, atomIndex: exp.atomIndex, interactive: this.state.interactive, interactiveProperties: {
                selectedVariableId: this.state.selectedVariableId,
                onVariableSelected: onVariableSelected,
                //TODO: Add calculations to get properties here!
                suggestedValue: undefined,
                actualValue: undefined,
                calculationResult: undefined,
                partialValue: undefined,
            } });
    };
}
//# sourceMappingURL=Component_PrettyPrint_FreeTextTerm.js.map